/**
 * « Tout mon suivi » — chantier 7, étape 4 (maquette 14, validée le 24/09).
 *
 * UNE page de suivi pour l'habitant : En cours / Terminées / Tout, filtres par type
 * (signalements, urbanisme, locations), chaque ligne mène à sa page détaillée. Les
 * services restent accessibles (accueil, « Nouvelle démarche »). Même source que la
 * carte « Ce que je suis » (lib/suivi-habitant).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppHeader } from '@/components/AppHeader';
import { chargerSuivi, LIB_TYPE, TON_CLASSE, type LigneSuivi, type TypeSuivi } from '@/lib/suivi-habitant';

type Onglet = 'encours' | 'terminees' | 'tout';

export function MonSuiviPage() {
  const [lignes, setLignes] = useState<LigneSuivi[] | null>(null);
  const [onglet, setOnglet] = useState<Onglet>('encours');
  const [type, setType] = useState<TypeSuivi | 'tout'>('tout');
  useEffect(() => { void chargerSuivi().then(setLignes); }, []);
  const vues = useMemo(() => (lignes ?? [])
    .filter((l) => (onglet === 'tout' ? true : onglet === 'encours' ? l.enCours : !l.enCours))
    .filter((l) => type === 'tout' || l.type === type), [lignes, onglet, type]);
  const groupes = (['signalement', 'urbanisme', 'location'] as TypeSuivi[]).map((t) => [t, vues.filter((l) => l.type === t)] as const).filter(([, l]) => l.length > 0);

  return (
    <div className="min-h-screen">
      <AppHeader module="Mon suivi" accent="grc" />
      <main id="main-content" className="mx-auto max-w-2xl space-y-4 px-4 py-6">
        <div>
          <h1 className="font-display text-xl font-semibold text-ink">Tout mon suivi</h1>
          <p className="mt-1 text-sm text-ink-muted">Toutes mes démarches, au même endroit.</p>
        </div>
        <div role="tablist" aria-label="Période" className="flex gap-1 rounded-r-pill border border-border p-1">
          {([['encours', 'En cours'], ['terminees', 'Terminées'], ['tout', 'Tout']] as const).map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={onglet === k} onClick={() => setOnglet(k)}
              className={`h-10 flex-1 rounded-r-pill text-sm font-medium ${onglet === k ? 'bg-grc/20 text-grc-fg' : 'text-ink-muted'}`}>{l}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5" aria-label="Type de démarche">
          {(['tout', 'signalement', 'urbanisme', 'location'] as const).map((t) => (
            <button key={t} type="button" aria-pressed={type === t} onClick={() => setType(t)}
              className={`h-8 rounded-r-pill border px-3 text-xs ${type === t ? 'border-grc bg-grc/15 font-medium text-ink' : 'border-border text-ink-muted'}`}>
              {t === 'tout' ? 'Tout' : LIB_TYPE[t]}
            </button>
          ))}
        </div>
        {lignes === null && <p className="text-sm text-ink-muted">Chargement…</p>}
        {lignes && groupes.length === 0 && <p className="rounded-r-xl border border-border p-4 text-sm text-ink-muted">Rien à afficher ici.</p>}
        {groupes.map(([t, l]) => (
          <section key={t} aria-label={LIB_TYPE[t]} className="rounded-r-xl border border-border bg-surface/55 p-4">
            <h2 className="mb-1 text-base font-semibold text-ink">{LIB_TYPE[t]}</h2>
            <ul>
              {l.map((x) => (
                <li key={x.id} className="border-t border-border/60 first:border-t-0">
                  <Link to={x.lien} className="flex items-center gap-3 py-3 text-sm text-ink hover:text-accent">
                    <span className="min-w-0 flex-1 truncate">{x.titre}</span>
                    <span className={`flex-shrink-0 rounded-r-pill px-2.5 py-0.5 text-2xs font-medium ${TON_CLASSE[x.ton]}`}>{x.statut}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <Link to="/" className="flex h-12 items-center justify-center rounded-r-xl border border-border bg-surface/55 text-sm font-medium text-ink hover:border-grc/50">
          + Nouvelle démarche
        </Link>
      </main>
    </div>
  );
}
